import React, { useState } from "react";
import {
    Alert,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    List,
    ListItem,
    TextField,
    Typography,
} from "@mui/material";
import { LoadingButton } from "@mui/lab";
import companyServices from "app/services/company-services";
import { useSnackbar } from "notistack";

const CONFIRM_WORD = "ELIMINAR";

// Dar de baja no borra: la compania y toda su operacion quedan en la base, y se
// puede revertir. El texto del modal dice exactamente eso, para que nadie crea que
// esto libera un RUT o borra pedidos.
//
// Borrar de verdad existe y es otro modal -CompanyDeleteModal-, que solo aparece
// en una compania ya dada de baja.
const CompanyDeactivateModal = ({
    company = null,
    open = false,
    onClose = () => {},
    onDeactivated = () => {},
}) => {
    const { enqueueSnackbar } = useSnackbar();
    const [confirmText, setConfirmText] = useState("");
    const [saving, setSaving] = useState(false);

    const handleClose = () => {
        setConfirmText("");
        setSaving(false);
        onClose();
    };

    const confirmed = confirmText.trim().toUpperCase() === CONFIRM_WORD;

    const deactivate = async () => {
        if (!confirmed || !company) return;

        try {
            setSaving(true);
            await companyServices.deactivateCompany(company.id);
            enqueueSnackbar(`${company.name} quedó dada de baja`, { variant: "success" });
            onDeactivated();
            handleClose();
        } catch (_error) {
            // El interceptor muestra el mensaje.
        } finally {
            setSaving(false);
        }
    };

    return (
        <Dialog fullWidth maxWidth="sm" open={open} onClose={handleClose}>
            <DialogTitle>
                <Typography variant="h2" color="error">Dar de baja compañía</Typography>
            </DialogTitle>

            <DialogContent>
                <Typography variant="body1" sx={{ mb: 2 }}>
                    <strong>{company?.name}</strong> quedará fuera de circulación:
                </Typography>

                <List dense disablePadding sx={{ mb: 2 }}>
                    <ListItem sx={{ py: 0.25 }}>
                        <Typography variant="body2">· Desaparece del selector de compañías.</Typography>
                    </ListItem>
                    <ListItem sx={{ py: 0.25 }}>
                        <Typography variant="body2">· Sus usuarios ya no podrán entrar en ella.</Typography>
                    </ListItem>
                    <ListItem sx={{ py: 0.25 }}>
                        <Typography variant="body2">· Quien esté parado en ella se moverá a otra de sus compañías.</Typography>
                    </ListItem>
                </List>

                <Alert severity="info" sx={{ mb: 2 }}>
                    Los datos se conservan: pedidos, viajes, entregas, clientes y bodegas quedan
                    intactos. Se puede reactivar después.
                </Alert>

                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                    Escribe <strong>{CONFIRM_WORD}</strong> para confirmar:
                </Typography>
                <TextField
                    fullWidth
                    size="small"
                    value={confirmText}
                    autoComplete="off"
                    onChange={(event) => setConfirmText(event.target.value)}
                    placeholder={CONFIRM_WORD}
                    error={confirmText.length > 0 && !confirmed}
                />
            </DialogContent>

            <DialogActions>
                <Button onClick={handleClose}>Cancelar</Button>
                <LoadingButton
                    loading={saving}
                    variant="contained"
                    color="error"
                    size="small"
                    disabled={!confirmed}
                    onClick={deactivate}
                >
                    {saving ? "Dando de baja" : "Dar de baja"}
                </LoadingButton>
            </DialogActions>
        </Dialog>
    );
};

export default CompanyDeactivateModal;
